import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Plus, X, Cake, UserPlus, CaretRight, Check, UploadSimple, Trash, ArrowCounterClockwise, Warning } from "@phosphor-icons/react";
import ImportPlayersModal from "@/components/ImportPlayersModal";

function age(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  return Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
}

export default function AdminPlayers() {
  const { user } = useAuth();
  const [players, setPlayers] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState({ name: "", date_of_birth: "" });
  const [err, setErr] = useState("");
  const [savingId, setSavingId] = useState("");
  const [justSaved, setJustSaved] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const isAdmin = ["head_coach", "super_admin"].includes(user?.role);

  const refresh = async () => {
    const [p, a, u] = await Promise.all([
      api.get("/players", { params: { include_archived: isAdmin ? true : undefined } }),
      api.get("/coach-assignments"),
      api.get("/users"),
    ]);
    setPlayers(p.data);
    setAssignments(a.data);
    setUsers(u.data);
  };

  useEffect(() => { refresh(); }, []);

  const coaches = useMemo(
    () => users.filter((u) => ["coach", "head_coach", "external_coach"].includes(u.role)),
    [users],
  );
  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  // For each player, find the active coach assignments
  const coachesByPlayer = useMemo(() => {
    const map = {};
    for (const a of assignments) {
      if (a.status !== "active") continue;
      (map[a.player_id] ||= []).push(a);
    }
    return map;
  }, [assignments]);

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/players", form);
      setOpen(false);
      setForm({ name: "", date_of_birth: "" });
      refresh();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail));
    }
  };

  const assignCoach = async (playerId, coachUserId) => {
    if (!coachUserId) return;
    setSavingId(playerId);
    setErr("");
    try {
      await api.post("/coach-assignments", {
        player_id: playerId,
        coach_user_id: coachUserId,
        type: "club",
      });
      await refresh();
      setJustSaved(playerId);
      setTimeout(() => setJustSaved((cur) => (cur === playerId ? "" : cur)), 1800);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setSavingId("");
    }
  };

  const unassignCoach = async (assignmentId) => {
    setErr("");
    try {
      await api.delete(`/coach-assignments/${assignmentId}`);
      refresh();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  return (
    <div className="space-y-5" data-testid="club-players">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-label">Club roster</div>
          <h1 className="font-heading uppercase text-3xl sm:text-4xl tracking-tight">Players</h1>
          {players && (
            <div className="text-xs text-zinc-500 mt-2">
              {players.filter((p) => p.active !== false).length} player{players.filter((p) => p.active !== false).length === 1 ? "" : "s"} ·{" "}
              {Object.keys(coachesByPlayer).length} with a coach
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button className="btn-secondary" onClick={() => setImportOpen(true)} data-testid="import-players-btn">
            <UploadSimple size={16} weight="bold" /> Import from Excel
          </button>
          <button className="btn-primary" onClick={() => setOpen(true)} data-testid="new-player-btn">
            <Plus size={16} weight="bold" /> New player
          </button>
        </div>
      </div>

      {err && (
        <div className="text-sm text-[#ff0026] bg-[#ff0026]/10 border border-[#ff0026]/30 rounded-md p-3" data-testid="assign-error">
          {err}
        </div>
      )}

      {!players && <div className="skeleton h-32" />}
      {players?.length === 0 && (
        <div className="surface p-8 text-center text-zinc-400">No players in this club yet.</div>
      )}

      {(() => {
        if (!players) return null;
        const active = players.filter((p) => p.active !== false);
        const archived = players.filter((p) => p.active === false);
        return (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              {active.map((p) => renderPlayerCard(p, {
                assigns: coachesByPlayer[p.id] || [],
                userMap, coaches, justSaved, savingId, isAdmin,
                onAssign: assignCoach, onUnassign: unassignCoach, onDelete: setDeleting,
              }))}
            </div>

            {isAdmin && archived.length > 0 && (
              <section data-testid="archived-players-section" className="pt-4">
                <button
                  onClick={() => setShowArchived((v) => !v)}
                  className="btn-ghost text-xs uppercase tracking-wider text-zinc-400"
                  data-testid="toggle-archived"
                >
                  <CaretRight
                    size={12}
                    weight="bold"
                    style={{ transform: showArchived ? "rotate(90deg)" : "none", transition: "transform 120ms" }}
                  />
                  Archived players ({archived.length})
                </button>
                {showArchived && (
                  <div className="grid gap-3 sm:grid-cols-2 mt-3 opacity-70">
                    {archived.map((p) => (
                      <div key={p.id} className="surface p-4 flex items-center justify-between gap-3" data-testid={`archived-player-${p.id}`}>
                        <div className="min-w-0">
                          <div className="font-semibold truncate">{p.name}</div>
                          <div className="text-xs text-zinc-500">Archived · data preserved</div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button
                            onClick={async () => {
                              try { await api.post(`/players/${p.id}/unarchive`); refresh(); }
                              catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
                            }}
                            className="btn-ghost text-xs"
                            data-testid={`unarchive-${p.id}`}
                            title="Restore player"
                          >
                            <ArrowCounterClockwise size={14} weight="bold" /> Restore
                          </button>
                          <button
                            onClick={() => setDeleting(p)}
                            className="btn-ghost text-[#ff0026] text-xs"
                            data-testid={`delete-archived-${p.id}`}
                            title="Delete permanently"
                          >
                            <Trash size={14} weight="bold" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}
          </>
        );
      })()}

      {open && (
        <div
          className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="new-player-modal"
        >
          <form onSubmit={save} className="surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading uppercase text-xl">New player</h2>
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="font-label block mb-1">Name</label>
                <input required className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-player-name" />
              </div>
              <div>
                <label className="font-label block mb-1">Date of birth</label>
                <input type="date" required className="input" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="new-player-dob" />
              </div>
              {err && <div className="text-sm text-[#ff0026]">{err}</div>}
              <button type="submit" className="btn-primary w-full" data-testid="new-player-submit">Create</button>
            </div>
          </form>
        </div>
      )}

      <ImportPlayersModal
        clubId={user?.club_id}
        clubName="this club"
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => refresh()}
      />

      {deleting && (
        <DeletePlayerModal
          player={deleting}
          onClose={() => setDeleting(null)}
          onDone={() => { setDeleting(null); refresh(); }}
        />
      )}
    </div>
  );
}


function renderPlayerCard(p, ctx) {
  const { assigns, userMap, coaches, justSaved, savingId, isAdmin, onAssign, onUnassign, onDelete } = ctx;
  const yrs = age(p.date_of_birth);
  return (
    <div key={p.id} className="surface p-4 space-y-3" data-testid={`admin-player-${p.id}`}>
      <div className="flex items-start justify-between gap-3">
        <Link
          to={`/players/${p.id}`}
          className="flex items-center gap-3 min-w-0 group"
          data-testid={`open-player-${p.id}`}
        >
          <div className="w-11 h-11 rounded-md bg-[#1a1a1a] border border-[#27272a] flex items-center justify-center font-heading text-lg shrink-0">
            {p.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
          </div>
          <div className="min-w-0">
            <div className="font-semibold truncate group-hover:text-[#dfff00]">{p.name}</div>
            <div className="text-xs text-zinc-500 flex items-center gap-2 mt-0.5">
              {yrs != null && <span className="flex items-center gap-1"><Cake size={12} weight="bold" /> {yrs} yrs</span>}
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-1 shrink-0">
          {isAdmin && (
            <button
              onClick={() => onDelete(p)}
              className="btn-ghost text-[#ff0026]"
              data-testid={`delete-player-${p.id}`}
              title="Archive or delete player"
            >
              <Trash size={14} weight="bold" />
            </button>
          )}
          <Link to={`/players/${p.id}`} className="btn-ghost" data-testid={`go-player-${p.id}`}>
            <CaretRight size={14} weight="bold" />
          </Link>
        </div>
      </div>

      {/* Coach assignment row */}
      <div className="border-t border-[#27272a] pt-3 space-y-2">
        <div className="font-label flex items-center justify-between">
          <span>Coach{assigns.length > 1 ? "es" : ""}</span>
          {justSaved === p.id && (
            <span className="flex items-center gap-1 text-[#16ec06] text-[10px] font-bold uppercase tracking-wider" data-testid={`saved-${p.id}`}>
              <Check size={11} weight="bold" /> Saved
            </span>
          )}
        </div>
        {assigns.length === 0 ? (
          <div className="text-xs text-zinc-500 italic">No coach assigned</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {assigns.map((a) => (
              <span
                key={a.id}
                className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#dfff00]/10 border border-[#dfff00]/20 text-[#dfff00] rounded text-xs"
                data-testid={`coach-pill-${a.id}`}
              >
                {userMap[a.coach_user_id]?.name || "?"}
                <button
                  onClick={() => onUnassign(a.id)}
                  className="hover:bg-[#ff0026]/20 rounded p-0.5"
                  aria-label={`Unassign ${userMap[a.coach_user_id]?.name || "coach"}`}
                  data-testid={`unassign-${a.id}`}
                >
                  <X size={11} weight="bold" />
                </button>
              </span>
            ))}
          </div>
        )}

        <AssignControl
          playerId={p.id}
          coaches={coaches}
          excluded={new Set(assigns.map((a) => a.coach_user_id))}
          busy={savingId === p.id}
          onAssign={(coachId) => onAssign(p.id, coachId)}
        />
      </div>
    </div>
  );
}


function DeletePlayerModal({ player, onClose, onDone }) {
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const isAlreadyArchived = player.active === false;

  const doArchive = async () => {
    setBusy("archive"); setErr("");
    try { await api.post(`/players/${player.id}/archive`); onDone(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); setBusy(""); }
  };
  const doDelete = async () => {
    setBusy("delete"); setErr("");
    try { await api.delete(`/players/${player.id}`); onDone(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); setBusy(""); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-4" onClick={onClose} data-testid="delete-player-modal">
      <div className="surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Warning size={20} weight="fill" className="text-[#ff0026]" />
            <h2 className="font-heading uppercase text-xl">Remove {player.name}?</h2>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost"><X size={18} /></button>
        </div>

        {!isAlreadyArchived && (
          <div className="mb-4 p-3 rounded-md bg-[#dfff00]/5 border border-[#dfff00]/20 text-sm text-zinc-300">
            <div className="font-bold text-[#dfff00] mb-1 uppercase text-xs tracking-wider">Archive (recommended)</div>
            Hides {player.name} from squads, evaluation lists and coach dashboards, but keeps every evaluation, goal and LTA record intact. Fully reversible.
          </div>
        )}

        <div className="mb-4 p-3 rounded-md bg-[#ff0026]/5 border border-[#ff0026]/30 text-sm text-zinc-300">
          <div className="font-bold text-[#ff0026] mb-1 uppercase text-xs tracking-wider">Delete permanently</div>
          Wipes {player.name} and <strong>every trace</strong> of them — evaluations, goals, coach assignments, parent links, squad memberships and LTA cache. <strong>No undo.</strong>
        </div>

        {err && <div className="text-sm text-[#ff0026] mb-3" data-testid="delete-player-error">{err}</div>}

        <div className="flex flex-col gap-2">
          {!isAlreadyArchived && (
            <button
              onClick={doArchive}
              disabled={!!busy}
              className="btn-primary w-full"
              data-testid="confirm-archive-player"
            >
              {busy === "archive" ? "Archiving…" : "Archive player"}
            </button>
          )}
          <button
            onClick={doDelete}
            disabled={!!busy}
            className="w-full px-4 py-2 border border-[#ff0026]/40 text-[#ff0026] hover:bg-[#ff0026]/10 rounded-md text-sm font-bold uppercase tracking-wider transition-colors"
            data-testid="confirm-delete-player"
          >
            {busy === "delete" ? "Deleting…" : "Delete permanently"}
          </button>
          <button onClick={onClose} disabled={!!busy} className="btn-ghost w-full">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function AssignControl({ playerId, coaches, excluded, busy, onAssign }) {
  const [pick, setPick] = useState("");
  const available = coaches.filter((c) => !excluded.has(c.id));
  if (available.length === 0) {
    return (
      <div className="text-[11px] text-zinc-600 italic">All available coaches assigned</div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <select
        className="input"
        style={{ height: "2.25rem", fontSize: "0.8rem" }}
        value={pick}
        onChange={(e) => setPick(e.target.value)}
        data-testid={`assign-select-${playerId}`}
        disabled={busy}
      >
        <option value="">Assign coach…</option>
        {available.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name} {c.role === "head_coach" ? "· head coach" : c.role === "external_coach" ? "· external" : ""}
          </option>
        ))}
      </select>
      <button
        onClick={() => {
          if (!pick) return;
          onAssign(pick);
          setPick("");
        }}
        disabled={!pick || busy}
        className="btn-secondary text-xs shrink-0"
        style={{ height: "2.25rem" }}
        data-testid={`assign-btn-${playerId}`}
      >
        <UserPlus size={13} weight="bold" /> Assign
      </button>
    </div>
  );
}
